import React from 'react';
import { Languages, Users, Clock, Globe, MessageCircle, CheckCircle } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import ServicePageLayout from './ServicePageLayout';
import { useTranslation } from '../../hooks/useTranslation';

export default function LanguageInterpreter() {
  const { t } = useTranslation();
  
  return (
    <ServicePageLayout
      title={t('services.languageInterpreter.title')}
      subtitle={t('services.languageInterpreter.subtitle')}
      icon={Languages}
      heroColor="from-teal-500 to-cyan-600"
      enquiryTitle={t('services.languageInterpreter.title')}
    >
      {/* Main Description */}
      <Card className="mb-8 shadow-lg" data-testid="service-main-card">
        <CardContent className="p-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">{t('services.languageInterpreter.mainTitle')}</h2>
          <p className="text-lg text-gray-700 leading-relaxed mb-4">
            {t('services.languageInterpreter.mainDesc1')}
          </p>
          <p className="text-lg text-gray-700 leading-relaxed">
            {t('services.languageInterpreter.mainDesc2')}
          </p>
        </CardContent>
      </Card>

      {/* Features Grid */}
      <div className="grid md:grid-cols-2 gap-6 mb-12">
        <Card className="hover:shadow-xl transition-shadow" data-testid="feature-card-languages">
          <CardContent className="p-6">
            <Globe className="w-12 h-12 text-teal-600 mb-4" />
            <h3 className="text-xl font-bold text-gray-900 mb-3">{t('services.languageInterpreter.features.languages.title')}</h3>
            <p className="text-gray-700">
              {t('services.languageInterpreter.features.languages.desc')}
            </p>
          </CardContent>
        </Card>

        <Card className="hover:shadow-xl transition-shadow" data-testid="feature-card-medical">
          <CardContent className="p-6">
            <Users className="w-12 h-12 text-teal-600 mb-4" />
            <h3 className="text-xl font-bold text-gray-900 mb-3">{t('services.languageInterpreter.features.medical.title')}</h3>
            <p className="text-gray-700">
              {t('services.languageInterpreter.features.medical.desc')}
            </p>
          </CardContent>
        </Card>

        <Card className="hover:shadow-xl transition-shadow" data-testid="feature-card-availability">
          <CardContent className="p-6">
            <Clock className="w-12 h-12 text-teal-600 mb-4" />
            <h3 className="text-xl font-bold text-gray-900 mb-3">{t('services.languageInterpreter.features.options.title')}</h3>
            <p className="text-gray-700">
              {t('services.languageInterpreter.features.options.desc')}
            </p>
          </CardContent>
        </Card>

        <Card className="hover:shadow-xl transition-shadow" data-testid="feature-card-support">
          <CardContent className="p-6">
            <MessageCircle className="w-12 h-12 text-teal-600 mb-4" />
            <h3 className="text-xl font-bold text-gray-900 mb-3">{t('services.languageInterpreter.features.documents.title')}</h3>
            <p className="text-gray-700">
              {t('services.languageInterpreter.features.documents.desc')}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Services Include */}
      <Card className="mb-8 shadow-lg" data-testid="services-included-card">
        <CardContent className="p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">{t('services.languageInterpreter.servicesInclude')}</h2>
          <div className="grid md:grid-cols-2 gap-4">
            {t('services.languageInterpreter.servicesList').map((service) => (
              <div key={service} className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-teal-500 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700">{service}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Languages Available */}
      <Card className="mb-8 shadow-lg" data-testid="languages-card">
        <CardContent className="p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">{t('services.languageInterpreter.languagesTitle')}</h2>
          <div className="flex flex-wrap gap-3">
            {t('services.languageInterpreter.languagesList').map((language) => (
              <span 
                key={language} 
                className="px-4 py-2 bg-teal-50 text-teal-700 rounded-full text-sm font-medium border border-teal-200"
              >
                {language}
              </span>
            ))}
          </div>
          <p className="text-gray-600 mt-4 text-sm">{t('services.languageInterpreter.languagesNote')}</p>
        </CardContent>
      </Card>
    </ServicePageLayout>
  );
}
